import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { LuClipboard } from 'react-icons/lu';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { InviteStatus } from '@ndvr/auth-api';
import { PersonOrg } from '@ndvr/customer-api';

import { useCopyGuid } from '@/components/CopyGuidComponents';
import { EntityUnavailable, isHttpError, useForgetNotFoundEntity } from '@/components/EntityUnavailable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UniversalSearchButton } from '@/components/UniversalSearchButton';
import { IndividualHeaderNav, InviteButton } from 'modules';
import {
    formatDate,
    getPersonOrgName,
    PERSON_STATUS_TAG_MAP,
    personListRoute,
    titleCase,
    useRequiredParams,
} from 'utils';
import { HorizontalLayout, IconTrash, MailTo, PageSpinner, PhoneNumber, Tag, Text, VerticalLayout } from 'widgets';
import { DeleteValidationModal } from 'widgets/delete/DeleteValidationModal';
import { DropdownMenu } from 'widgets/Dropdown';

import {
    removePersonQueries,
    useDeletePersonOrg,
    useInviteDetails,
    usePersonCredential,
    usePersonOrgProfile,
} from './person-queries';
import { AffiliationsTab } from './PersonAffiliations';
import { PersonDetailsTab } from './PersonDetailsTab';
import { JournalTab } from './PersonJournal';
import { PortfoliosTab } from './PersonPortfoliosTab';
import { PersonToolsTab } from './personToolsTab/PersonToolsTab';

const tabs = [
    {
        title: 'Households',
        url: '/households',
        Component: <PortfoliosTab />,
    },
    {
        title: 'Details',
        url: '/details',
        Component: <PersonDetailsTab />,
    },
    {
        title: 'Journal',
        url: '/journal',
        Component: <JournalTab />,
    },
    {
        title: 'Tools',
        url: '/tools',
        Component: <PersonToolsTab />,
    },
    {
        title: 'Affiliations',
        url: '/affiliations',
        Component: <AffiliationsTab />,
    },
];

export const Person = () => {
    const { personOrgGUID, tab } = useRequiredParams(['personOrgGUID', 'tab']);
    const { data: person, error } = usePersonOrgProfile(personOrgGUID);
    // Only once the person is known to exist, so a missing person does not fail these requests too.
    const { data: inviteDetails } = useInviteDetails(person ? personOrgGUID : undefined);
    const { data: credential } = usePersonCredential(person ? personOrgGUID : undefined);

    useForgetNotFoundEntity(personOrgGUID, error);

    const baseUrl = `/person/${personOrgGUID}`;
    const activeTab = tabs.find(({ url }) => url.slice(1) === tab);

    // A failed refetch keeps the loaded person on screen, along with any open delete dialog.
    if (!person) {
        if (!error) {
            return <PageSpinner />;
        }
        // Without a PersonOrg row there is no tenant to authorize against, so a deleted person answers 403 to
        // everyone but themselves, who get 404.
        return (
            <EntityUnavailable
                title={
                    isHttpError(error, 403, 404)
                        ? "This person doesn't exist or you don't have access to it"
                        : "Couldn't load this person"
                }
                backTo={personListRoute}
                backLabel="Back to persons"
            />
        );
    }

    if (!activeTab) {
        return <Navigate to={`${baseUrl}/households`} replace />;
    }

    const personName = getPersonOrgName(person.personOrg);

    return (
        <VerticalLayout $stretch>
            <IndividualHeaderNav
                guid={personOrgGUID}
                breadcrumbs={
                    person.personOrg.commandCenterUser
                        ? [
                              { label: 'Claro Intelligent Hub Users', path: '/admin/internal-users' },
                              { label: personName, path: '#' },
                          ]
                        : [
                              { label: 'Persons', path: personListRoute },
                              { label: personName, path: '#' },
                          ]
                }
                title={personName}
                infoBlocks={[
                    {
                        component: (
                            <VerticalLayout $gap={4}>
                                <MailTo address={person.personOrg.primaryEmail?.plainText} />
                                <PhoneNumber phoneNumber={person.personOrg.personalPhone?.plainText} />
                            </VerticalLayout>
                        ),
                    },
                    { label: 'last updated', value: formatDate(person.personOrg.updatedAt) },
                ]}
                actions={
                    inviteDetails ? (
                        <HorizontalLayout $gap={32} $rowCenter>
                            <UniversalSearchButton />
                            {credential?.disabled ? (
                                <Tag type="negative">DISABLED</Tag>
                            ) : (
                                <Tag type={PERSON_STATUS_TAG_MAP[inviteDetails.inviteStatus]}>
                                    {titleCase(inviteDetails.inviteStatus)}
                                </Tag>
                            )}

                            {inviteDetails.inviteStatus === InviteStatus.REGISTERED ? (
                                <VerticalLayout>
                                    <Text>{formatDate(credential?.lastLoggedInAt)}</Text>
                                    <Text helper $uppercase $themeColor="subtle">
                                        last login
                                    </Text>
                                </VerticalLayout>
                            ) : (
                                <InviteButton
                                    buttonSize="medium"
                                    personOrgProfile={person}
                                    inviteDetails={inviteDetails}
                                />
                            )}
                            <PersonOptionsMenu personOrg={person.personOrg} />
                        </HorizontalLayout>
                    ) : undefined
                }
            />
            <Tabs value={tab} className="flex flex-1 flex-col overflow-hidden">
                <TabsList className="flex-shrink-0">
                    {tabs.map(({ title, url }) => {
                        const tabValue = url.slice(1);
                        return (
                            <TabsTrigger key={title} value={tabValue} asChild>
                                <Link to={`${baseUrl}${url}`} replace>
                                    {title}
                                </Link>
                            </TabsTrigger>
                        );
                    })}
                </TabsList>
                <TabsContent className="overflow-auto p-6" value={tab}>
                    {activeTab?.Component}
                </TabsContent>
            </Tabs>
        </VerticalLayout>
    );
};

const PersonOptionsMenu = ({ personOrg }: { personOrg: PersonOrg }) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const isDeleted = useRef(false);
    const { personOrgGUID } = personOrg;
    // This menu unmounts with the page after a deletion. Unmounting views cannot refetch what is dropped here; a
    // mounted page would refetch the deleted person on its next render.
    useEffect(
        () => () => {
            if (isDeleted.current) {
                removePersonQueries(queryClient, personOrgGUID);
            }
        },
        [queryClient, personOrgGUID],
    );
    const { copyGuid, copyGuidLabel } = useCopyGuid(personOrg.personOrgGUID);

    const { mutateAsync: deletePersonOrg, isPending: isDeletePending } = useDeletePersonOrg(personOrg.personOrgGUID);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    return (
        <>
            <DropdownMenu
                placement="bottom-end"
                buttons={[
                    {
                        button: (
                            <HorizontalLayout $apart $rowCenter>
                                <Text>{copyGuidLabel}</Text>
                                <LuClipboard />
                            </HorizontalLayout>
                        ),
                        onClick: copyGuid,
                    },
                    {
                        button: (
                            <HorizontalLayout $apart $rowCenter>
                                <Text>Delete person</Text>
                                <IconTrash size="micro" />
                            </HorizontalLayout>
                        ),
                        onClick: () => setIsDeleteModalOpen(true),
                    },
                ]}
            />
            {isDeleteModalOpen && (
                <DeleteValidationModal
                    recordName={getPersonOrgName(personOrg)}
                    disabled={isDeletePending}
                    onDelete={deletePersonOrg}
                    onClose={(result) => {
                        setIsDeleteModalOpen(false);
                        if (result?.valid) {
                            isDeleted.current = true;
                            navigate(personListRoute);
                        }
                    }}
                />
            )}
        </>
    );
};
